<script setup lang="ts">
/**
 * ToastViewport — fixed-position container for the singleton toast
 * queue. Mount once in layouts/AppLayout (the always-on shell); any code
 * pushing through useToasts() shows up here.
 *
 * The whole region is role="status" aria-live="polite" so a single
 * toast is both visible AND audible — replaces the parallel sr-only
 * announcement blocks the views used to render. role="status" is
 * preferred over role="alert" because most toasts are confirmations
 * (success), not interruptions; alerts would yank focus from the
 * action the user just took.
 */
import { useI18n } from "vue-i18n";
import { useToasts } from "@/composables/useToasts";
import {
  CheckCircleIcon as Check,
  ExclamationCircleIcon as Alert,
  InformationCircleIcon as Info,
  XMarkIcon as Close,
} from "@heroicons/vue/24/outline";

const { t } = useI18n({ useScope: 'global' });
const { toasts, dismiss } = useToasts();

const KIND_STYLES = {
  success:
    "border-status-success/50 bg-status-success/10 text-status-success",
  error:
    "border-status-error/50 bg-status-error/10 text-status-error",
  info:
    "border-border bg-surface-overlay text-content-primary",
} as const;
const KIND_ICONS = {
  success: Check,
  error: Alert,
  info: Info,
} as const;
</script>

<template>
  <!-- pointer-events-none on the wrapper so the empty space below the
       stack doesn't intercept clicks meant for the page; toasts
       themselves re-enable pointer-events. -->
  <div
    class="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2
           max-w-[calc(100vw-2rem)] w-80"
    role="status"
    aria-live="polite"
    :aria-label="t('a11y.notifications')"
  >
    <transition-group
      name="toast"
      tag="div"
      class="flex flex-col gap-2"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="pointer-events-auto rounded-lg border shadow-elevated px-3 py-2 text-sm
               flex items-start gap-2"
        :class="KIND_STYLES[toast.kind]"
      >
        <component
          :is="KIND_ICONS[toast.kind]"
          class="h-4 w-4 shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <p class="flex-1 min-w-0 break-words">{{ toast.message }}</p>
        <button
          type="button"
          class="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
          :aria-label="t('a11y.dismissNotification')"
          @click="dismiss(toast.id)"
        >
          <Close class="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
/* Slide-in from the right + fade. Linear-style. */
.toast-enter-active,
.toast-leave-active {
  transition:
    transform 180ms ease-out,
    opacity 180ms ease-out;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(0.75rem);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(0.75rem);
}
.toast-move {
  transition: transform 180ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move {
    transition: opacity 80ms ease-out;
  }
  .toast-enter-from,
  .toast-leave-to {
    transform: none;
  }
}
</style>
